import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import _ from 'lodash';

import { AI_CHAT_ASSISTANT_SLOW_WARNING_MS } from 'constants/aiChat';

import { setAssistantSlowAction } from 'store/aiChat/actions';
import {
  selectAiChatAwaitingAssistantLastActivityAt,
  selectAiChatHasPendingApproval,
  selectAiChatIsAssistantSlow,
  selectAiChatIsAwaitingAssistantResponse,
} from 'store/aiChat/selectors';

export const useAssistantSlowWarning = () => {
  const dispatch = useDispatch();

  const isAwaiting = useSelector(selectAiChatIsAwaitingAssistantResponse);
  const lastActivityAt = useSelector(selectAiChatAwaitingAssistantLastActivityAt);
  const isSlow = useSelector(selectAiChatIsAssistantSlow);
  const hasPendingApproval = useSelector(selectAiChatHasPendingApproval);

  useEffect(() => {
    // While the turn is parked at an approval gate, the assistant is waiting on
    // the user — not working — so the inactivity timer must not fire, and any
    // warning already shown must clear. Treat it the same as "not awaiting".
    if (!isAwaiting || hasPendingApproval || _.isNil(lastActivityAt)) {
      if (isSlow) {
        dispatch(setAssistantSlowAction(false));
      }

      return _.noop;
    }

    const lastActivityMs = new Date(lastActivityAt).getTime();

    if (Number.isNaN(lastActivityMs)) {
      return _.noop;
    }

    const elapsedMs = Date.now() - lastActivityMs;
    const remainingMs = AI_CHAT_ASSISTANT_SLOW_WARNING_MS - elapsedMs;

    if (remainingMs <= 0) {
      if (!isSlow) {
        dispatch(setAssistantSlowAction(true));
      }

      return _.noop;
    }

    const timeoutId = setTimeout(() => {
      dispatch(setAssistantSlowAction(true));
    }, remainingMs);

    return () => clearTimeout(timeoutId);
  }, [isAwaiting, hasPendingApproval, lastActivityAt, isSlow, dispatch]);
};
